/* eslint-disable react-hooks/exhaustive-deps */
import { KTSVG } from '../../_metronic/helpers';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Link, useHistory } from 'react-router-dom';
import validationErrorMsg from '../helpers/ValidationErrorMsg';
import { clampToInventoryDateBounds, getInventoryDateBounds, inventoryDateError, inventoryDateHint, todayYmd } from '../helpers/inventoryDateBounds';

const ITEM_TYPE_OPTIONS = [
    { value: 'raw_material', label: 'Raw Material' },
    { value: 'finished_product', label: 'Finished Product' },
];

const SEARCH_LIMIT = 40;
const PAGE_SIZE = 150;
const STOCK_VALUE_ADJUSTMENT_TYPE_ID = 11;
const ALL_CATEGORY_VALUE = 'all';
const ALL_CATEGORY = { value: ALL_CATEGORY_VALUE, label: 'All' };
const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'Active', label: 'Active' },
    { value: 'Inactive', label: 'Inactive' },
];

const withAllCategory = (list: any[] = []) => [ALL_CATEGORY, ...list];
const isCategorySelected = (cat: any) => !!cat?.value;
const isAllCategory = (cat: any) => cat?.value === ALL_CATEGORY_VALUE;

interface AdjustmentRow {
    idItems: number;
    idProducts: number;
    itemName: string;
    itemCode: string;
    categoryName: string;
    unitName: string;
    stockQuantity: number;
    physicalQuantity: number;
    averageRate: number;
    [key: string]: any;
}

interface AdjustmentData {
    idProjects: number;
    idWarehouses: number;
    idItemStockAdjustmentType: number;
    itemType: string;
    adjustmentDate: string;
    remarks: string;
}

type PendingMap = Record<string, AdjustmentRow>;

const rowKey = (row: { idItems?: number; idProducts?: number }, finishedProduct: boolean) =>
    finishedProduct ? `p:${row.idProducts}` : `i:${row.idItems}`;

const getAdjQty = (row: AdjustmentRow) => {
    const phys = Number(row.physicalQuantity) || 0;
    const prev = Number(row.stockQuantity) || 0;
    return parseFloat((phys - prev).toFixed(2));
};

const isChangedRow = (row: AdjustmentRow) => Math.abs(getAdjQty(row)) >= 0.0001;

const mapOptionToRow = (option: any): AdjustmentRow => ({
    idItems: option.idItems || 0,
    idProducts: option.idProducts || 0,
    itemName: option.itemName,
    itemCode: option.itemCode || '',
    categoryName: option.categoryName ?? '',
    unitName: option.unitName || '',
    stockQuantity: option.stockQuantity ?? 0,
    physicalQuantity: option.stockQuantity ?? 0,
    averageRate: option.averageRate ?? 0,
    packing: option.packing ?? '',
    size: option.size ?? '',
});

const ItemStockAdjustmentCreate = () => {
    const history = useHistory();
    const today = todayYmd();

    const [projects, setProjects] = useState<any[]>([]);
    const [project, setProject] = useState<any>({ label: 'Select Project', value: '' });
    const [warehouses, setWarehouses] = useState<any[]>([]);
    const [warehouse, setWarehouse] = useState<any>({ label: 'Select Warehouse', value: '' });
    const [adjustmentTypes, setAdjustmentTypes] = useState<any[]>([]);
    const [adjustmentType, setAdjustmentType] = useState<any>({ label: 'Select Adjustment Type', value: '' });
    const [itemType, setItemType] = useState<any>(ITEM_TYPE_OPTIONS[0]);
    const [categories, setCategories] = useState<any[]>([]);
    const [category, setCategory] = useState<any>(null);
    const [status, setStatus] = useState<any>(STATUS_OPTIONS[1]);
    const [categoryTotal, setCategoryTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    const [adjustment, setAdjustment] = useState<AdjustmentData>({
        idProjects: 0,
        idWarehouses: 0,
        idItemStockAdjustmentType: 0,
        itemType: 'raw_material',
        adjustmentDate: today,
        remarks: '',
    });

    const [adjustmentRows, setAdjustmentRows] = useState<AdjustmentRow[]>([]);
    const [pendingChanges, setPendingChanges] = useState<PendingMap>({});
    const [manualRows, setManualRows] = useState<AdjustmentRow[]>([]);
    const [addSelectKey, setAddSelectKey] = useState(0);
    const [loadingCategoryItems, setLoadingCategoryItems] = useState(false);
    const [saving, setSaving] = useState(false);
    const itemSearchInputRef = useRef('');
    const pendingRef = useRef<PendingMap>({});
    const rowsRef = useRef<AdjustmentRow[]>([]);

    useEffect(() => { pendingRef.current = pendingChanges; }, [pendingChanges]);
    useEffect(() => { rowsRef.current = adjustmentRows; }, [adjustmentRows]);

    const isFinishedProduct = itemType?.value === 'finished_product';
    const isStockValueAdjustment = Number(adjustment.idItemStockAdjustmentType) === STOCK_VALUE_ADJUSTMENT_TYPE_ID;
    const categoryLabel = isFinishedProduct ? 'Product Category' : 'Item Category';
    const statusLabel = isFinishedProduct ? 'Product Status' : 'Item Status';
    const detailsTitle = isFinishedProduct ? 'Product Details' : 'Material Details';
    const nameColumnLabel = isFinishedProduct ? 'Name of Products' : 'Name of Materials';
    const codeColumnLabel = isFinishedProduct ? 'Product Code' : 'Item Code';
    const tableColSpan = isFinishedProduct ? 12 : 10;
    const searchPlaceholder = isFinishedProduct
        ? 'Search & add product by name or code...'
        : 'Search & add item by name or code...';
    const canSearchItems = !!project?.value && !!warehouse?.value && !!adjustment.adjustmentDate;

    useEffect(() => {
        getProjects();
        getAdjustmentTypes();
        getItemCategories();
    }, []);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => { if (resp.status && resp.data) setProjects(resp.data); })
            .catch(console.error);
    };

    const getWarehousesByProject = (idProjects: number) => {
        if (!idProjects) {
            setWarehouses([]);
            setWarehouse({ label: 'Select Warehouse', value: '' });
            return;
        }
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/warehouses/' + idProjects, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                setWarehouses(resp.status && resp.data ? resp.data : []);
                setWarehouse({ label: 'Select Warehouse', value: '' });
            })
            .catch(() => setWarehouses([]));
    };

    const getAdjustmentTypes = () => {
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/adjustment-types', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => { if (resp.status && resp.data) setAdjustmentTypes(resp.data); })
            .catch(console.error);
    };

    const getItemCategories = () => {
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/item-categories', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => setCategories(withAllCategory(resp.status && resp.data ? resp.data : [])))
            .catch(() => setCategories(withAllCategory()));
    };

    const getProductCategories = (idProjects: number) => {
        if (!idProjects) { setCategories(withAllCategory()); return; }
        const params = new URLSearchParams({ id_projects: String(idProjects) });
        fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment/product-categories?' + params, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => setCategories(withAllCategory(resp.status && resp.data ? resp.data : [])))
            .catch(() => setCategories(withAllCategory()));
    };

    const fetchStockList = useCallback((opts: {
        idProjects: number | string;
        idWarehouses: number | string;
        adjustmentDate: string;
        search?: string;
        categoryId?: number | string | null;
        finishedProduct?: boolean;
        limit?: number;
        page?: number;
        countOnly?: boolean;
        adjustmentTypeId?: number | string | null;
        status?: string;
    }): Promise<{ data: any[]; total: number; page: number; lastPage: number }> => {
        const params = new URLSearchParams({
            id_projects: String(opts.idProjects),
            id_warehouses: String(opts.idWarehouses),
            adjustment_date: opts.adjustmentDate,
            search: opts.search || '',
            limit: String(opts.limit ?? SEARCH_LIMIT),
            page: String(opts.page ?? 1),
            status: opts.status || 'Active',
        });
        if (opts.categoryId && opts.categoryId !== ALL_CATEGORY_VALUE) {
            if (opts.finishedProduct) params.set('id_product_categories', String(opts.categoryId));
            else params.set('id_item_categories', String(opts.categoryId));
        }
        if (opts.countOnly) params.set('count_only', '1');
        if (opts.adjustmentTypeId) {
            params.set('id_item_stock_adjustment_type', String(opts.adjustmentTypeId));
        }

        const endpoint = opts.finishedProduct
            ? 'item-stock-adjustment/products-with-stock?'
            : 'item-stock-adjustment/items-with-stock?';

        return fetch(process.env.REACT_APP_API_URL + endpoint + params, getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => ({
                data: resp.status && resp.data ? resp.data : [],
                total: typeof resp.total === 'number' ? resp.total : 0,
                page: resp.page || opts.page || 1,
                lastPage: resp.lastPage || 1,
            }))
            .catch(() => ({ data: [], total: 0, page: 1, lastPage: 1 }));
    }, []);

    const applyPendingToRows = (rows: AdjustmentRow[], pending: PendingMap, finishedProduct: boolean) =>
        rows.map((row) => {
            const key = rowKey(row, finishedProduct);
            return pending[key] ? { ...row, ...pending[key], stockQuantity: row.stockQuantity } : row;
        });

    const syncCurrentPageIntoPending = (rows: AdjustmentRow[], finishedProduct: boolean): PendingMap => {
        const next = { ...pendingRef.current };
        rows.forEach((row) => {
            const key = rowKey(row, finishedProduct);
            if (isChangedRow(row)) next[key] = { ...row };
            else delete next[key];
        });
        pendingRef.current = next;
        setPendingChanges(next);
        return next;
    };

    const loadCategoryPage = (
        selectedCategory: any,
        page: number,
        ctx?: {
            projectValue?: any;
            warehouseValue?: any;
            adjustmentDate?: string;
            finishedProduct?: boolean;
            adjustmentTypeId?: number | string | null;
            statusValue?: string;
            resetPending?: boolean;
        }
    ) => {
        const projectValue = ctx?.projectValue ?? project?.value;
        const warehouseValue = ctx?.warehouseValue ?? warehouse?.value;
        const adjustmentDate = ctx?.adjustmentDate ?? adjustment.adjustmentDate;
        const finishedProduct = ctx?.finishedProduct ?? isFinishedProduct;
        const adjustmentTypeId = ctx?.adjustmentTypeId ?? adjustment.idItemStockAdjustmentType;
        const statusValue = ctx?.statusValue ?? status?.value;

        if (!isCategorySelected(selectedCategory)) {
            setCategoryTotal(0);
            setCurrentPage(1);
            setLastPage(1);
            setAdjustmentRows([]);
            if (ctx?.resetPending !== false) {
                setPendingChanges({});
                pendingRef.current = {};
                setManualRows([]);
            }
            return;
        }

        if (!projectValue || !warehouseValue || !adjustmentDate) return;

        // Preserve edits from current page before navigating
        if (!ctx?.resetPending) {
            syncCurrentPageIntoPending(rowsRef.current, finishedProduct);
        }

        setLoadingCategoryItems(true);
        fetchStockList({
            idProjects: projectValue,
            idWarehouses: warehouseValue,
            adjustmentDate,
            categoryId: selectedCategory.value,
            finishedProduct,
            limit: PAGE_SIZE,
            page,
            adjustmentTypeId,
            status: statusValue,
        })
            .then((res) => {
                setCategoryTotal(res.total);
                setCurrentPage(res.page);
                setLastPage(res.lastPage);
                const pending = ctx?.resetPending ? {} : pendingRef.current;
                if (ctx?.resetPending) {
                    setPendingChanges({});
                    pendingRef.current = {};
                    setManualRows([]);
                }
                const mapped = res.data.map(mapOptionToRow);
                setAdjustmentRows(applyPendingToRows(mapped, pending, finishedProduct));
                setAddSelectKey((k) => k + 1);
                if (!res.data.length && page === 1) {
                    Swal.fire({
                        icon: 'info',
                        title: 'No data',
                        text: isAllCategory(selectedCategory)
                            ? (finishedProduct ? 'No products found.' : 'No items found.')
                            : (finishedProduct ? 'No products found in this category.' : 'No items found in this category.'),
                    });
                }
            })
            .finally(() => setLoadingCategoryItems(false));
    };

    const loadItemOptions = (inputValue: string): Promise<any[]> => {
        if (!canSearchItems) return Promise.resolve([]);
        const searchTerm = (inputValue || itemSearchInputRef.current || '').trim();
        if (!searchTerm && !isCategorySelected(category)) return Promise.resolve([]);

        return fetchStockList({
            idProjects: project.value,
            idWarehouses: warehouse.value,
            adjustmentDate: adjustment.adjustmentDate,
            search: searchTerm,
            categoryId: category?.value || null,
            finishedProduct: isFinishedProduct,
            limit: SEARCH_LIMIT,
            page: 1,
            adjustmentTypeId: adjustment.idItemStockAdjustmentType,
            status: status?.value,
        }).then((res) => {
            const existing = new Set([
                ...adjustmentRows.map((r) => rowKey(r, isFinishedProduct)),
                ...manualRows.map((r) => rowKey(r, isFinishedProduct)),
                ...Object.keys(pendingChanges),
            ]);
            return res.data.filter((opt: any) => {
                const key = rowKey(opt, isFinishedProduct);
                return !existing.has(key);
            });
        });
    };

    const clearAll = () => {
        setAdjustmentRows([]);
        setPendingChanges({});
        pendingRef.current = {};
        setManualRows([]);
        setCurrentPage(1);
        setLastPage(1);
        setCategoryTotal(0);
        setAddSelectKey((k) => k + 1);
    };

    const handleItemTypeChange = (selected: any) => {
        setItemType(selected);
        setAdjustment((prev) => ({ ...prev, itemType: selected?.value || 'raw_material' }));
        setCategory(null);
        clearAll();
        if (selected?.value === 'finished_product') getProductCategories(project?.value || 0);
        else getItemCategories();
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setAdjustment((prev) => ({
            ...prev,
            idProjects: selected?.value || 0,
            adjustmentDate: clampToInventoryDateBounds(prev.adjustmentDate, selected?.inventoryBackdateDays),
        }));
        getWarehousesByProject(selected?.value);
        setWarehouse({ label: 'Select Warehouse', value: '' });
        setCategory(null);
        clearAll();
        if (isFinishedProduct) getProductCategories(selected?.value || 0);
    };

    const handleWarehouseChange = (selected: any) => {
        setWarehouse(selected);
        setAdjustment((prev) => ({ ...prev, idWarehouses: selected?.value || 0 }));
        if (isCategorySelected(category) && selected?.value) {
            loadCategoryPage(category, 1, { warehouseValue: selected.value, resetPending: true });
        } else clearAll();
    };

    const handleCategoryChange = (selected: any) => {
        setCategory(selected);
        loadCategoryPage(selected, 1, { resetPending: true });
    };

    const handleStatusChange = (selected: any) => {
        const next = selected || STATUS_OPTIONS[1];
        setStatus(next);
        if (isCategorySelected(category)) {
            loadCategoryPage(category, 1, { statusValue: next.value, resetPending: true });
        }
    };

    const goToPage = (page: number) => {
        if (!isCategorySelected(category) || page < 1 || page > lastPage || page === currentPage || loadingCategoryItems) return;
        loadCategoryPage(category, page);
    };

    const handleAdjustmentTypeChange = (selected: any) => {
        setAdjustmentType(selected);
        const typeId = selected?.value || 0;
        setAdjustment((prev) => ({ ...prev, idItemStockAdjustmentType: typeId }));
        // Reload rates when switching to/from Stock Value Adjustment (edging closing rate)
        if (isCategorySelected(category) && project?.value && warehouse?.value) {
            loadCategoryPage(category, currentPage || 1, {
                adjustmentTypeId: typeId,
                resetPending: true,
            });
        }
    };

    const handleAddItem = (option: any) => {
        if (!option) return;
        const key = rowKey(option, isFinishedProduct);
        const onPage = adjustmentRows.some((r) => rowKey(r, isFinishedProduct) === key);
        const inManual = manualRows.some((r) => rowKey(r, isFinishedProduct) === key);
        if (onPage || inManual || pendingChanges[key]) {
            Swal.fire({ icon: 'info', title: 'Already added', text: 'Already in the list.', timer: 1400, showConfirmButton: false });
            setAddSelectKey((k) => k + 1);
            return;
        }
        const row = mapOptionToRow(option);
        // If not on current category page, keep in manual section
        if (!onPage) setManualRows((prev) => [...prev, row]);
        else setAdjustmentRows((prev) => [...prev, row]);
        setAddSelectKey((k) => k + 1);
    };

    const updateRowField = (index: number, field: 'physicalQuantity' | 'averageRate', value: number, source: 'page' | 'manual') => {
        const updater = (prev: AdjustmentRow[]) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            const row = updated[index];
            const key = rowKey(row, isFinishedProduct);
            setPendingChanges((p) => {
                const next = { ...p };
                if (isChangedRow(row) || source === 'manual') next[key] = { ...row };
                else if (source === 'page') delete next[key];
                pendingRef.current = next;
                return next;
            });
            return updated;
        };
        if (source === 'manual') setManualRows(updater);
        else setAdjustmentRows(updater);
    };

    const removePageRow = (index: number) => {
        const row = adjustmentRows[index];
        const key = rowKey(row, isFinishedProduct);
        setAdjustmentRows((prev) => prev.filter((_, i) => i !== index));
        setPendingChanges((p) => {
            const next = { ...p };
            delete next[key];
            pendingRef.current = next;
            return next;
        });
    };

    const removeManualRow = (index: number) => {
        const row = manualRows[index];
        const key = rowKey(row, isFinishedProduct);
        setManualRows((prev) => prev.filter((_, i) => i !== index));
        setPendingChanges((p) => {
            const next = { ...p };
            delete next[key];
            pendingRef.current = next;
            return next;
        });
    };

    const collectChangedRows = (scope: 'page' | 'all'): AdjustmentRow[] => {
        const synced = syncCurrentPageIntoPending(adjustmentRows, isFinishedProduct);
        if (scope === 'page') {
            const fromPage = adjustmentRows.filter(isChangedRow);
            // Manual rows always included with page save when changed
            const manualChanged = manualRows.filter(isChangedRow);
            return [...fromPage, ...manualChanged];
        }
        // all: pending + manual + current page
        const map: PendingMap = { ...synced };
        manualRows.forEach((r) => { map[rowKey(r, isFinishedProduct)] = r; });
        adjustmentRows.forEach((r) => {
            if (isChangedRow(r)) map[rowKey(r, isFinishedProduct)] = r;
        });
        return Object.values(map).filter(isChangedRow);
    };

    const saveAdjustment = async (scope: 'page' | 'all') => {
        if (!adjustment.itemType) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Item Type.' });
            return;
        }
        if (!adjustment.idProjects) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Project.' });
            return;
        }
        if (!adjustment.idWarehouses) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Warehouse.' });
            return;
        }
        if (!adjustment.idItemStockAdjustmentType) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Please select Adjustment Type.' });
            return;
        }
        const typeStillValid = adjustmentTypes.some((t: any) => Number(t.value) === Number(adjustment.idItemStockAdjustmentType));
        if (!typeStillValid) {
            Swal.fire({ icon: 'warning', title: 'Adjustment type outdated', text: 'Please re-select Adjustment Type.' });
            return;
        }
        const dateError = inventoryDateError(adjustment.adjustmentDate, project?.inventoryBackdateDays);
        if (dateError) {
            Swal.fire({ icon: 'error', title: 'Error', text: dateError });
            return;
        }

        const changedRows = collectChangedRows(scope);
        if (changedRows.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'No changes',
                text: scope === 'page'
                    ? 'Change Physical Qty on this page first.'
                    : 'No pending quantity changes to save.',
            });
            return;
        }

        const confirm = await Swal.fire({
            title: scope === 'page' ? 'Save this page?' : 'Save all pending?',
            html: scope === 'page'
                ? `Save <b>${changedRows.length}</b> change(s) from this page${manualRows.filter(isChangedRow).length ? ' (including manually added)' : ''}?`
                : `Save <b>${changedRows.length}</b> pending change(s) from all pages?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3699FF',
            confirmButtonText: scope === 'page' ? 'Yes, Save Page' : 'Yes, Save All',
            cancelButtonText: 'Cancel',
        });
        if (!confirm.isConfirmed) return;

        setSaving(true);
        const postData = {
            adjustment: {
                idProjects: adjustment.idProjects,
                idWarehouses: adjustment.idWarehouses,
                idItemStockAdjustmentType: adjustment.idItemStockAdjustmentType,
                itemType: adjustment.itemType,
                adjustmentDate: adjustment.adjustmentDate,
                remarks: adjustment.remarks || null,
            },
            adjustmentRows: changedRows.map((r) => ({
                idItems: r.idItems || null,
                idProducts: isFinishedProduct ? r.idProducts : null,
                stockQuantity: isNaN(Number(r.stockQuantity)) ? 0 : Number(r.stockQuantity),
                physicalQuantity: Number(r.physicalQuantity) >= 0 ? Number(r.physicalQuantity) : 0,
                averageRate: isStockValueAdjustment
                    ? (isNaN(Number(r.averageRate)) ? 0 : Number(r.averageRate))
                    : (Number(r.averageRate) >= 0 ? Number(r.averageRate) : 0),
            })),
        };

        try {
            const res = await fetch(process.env.REACT_APP_API_URL + 'item-stock-adjustment', postRequestOptions(postData));
            const data = await res.json();
            if (!data.success) {
                const msg = data.errors ? validationErrorMsg(data.errors).join('<br>') : (data.errorMessage || 'Failed to save.');
                Swal.fire({ icon: 'error', title: 'Error', html: msg });
                return;
            }

            // Clear saved keys from pending / reset physical to new stock for UX
            const savedKeys = new Set(changedRows.map((r) => rowKey(r, isFinishedProduct)));
            setPendingChanges((p) => {
                const next = { ...p };
                savedKeys.forEach((k) => delete next[k]);
                pendingRef.current = next;
                return next;
            });
            setManualRows((prev) => prev.filter((r) => !savedKeys.has(rowKey(r, isFinishedProduct))));

            if (scope === 'page' && isCategorySelected(category) && currentPage < lastPage) {
                const next = await Swal.fire({
                    icon: 'success',
                    title: 'Page saved',
                    html: `Saved ${changedRows.length} row(s).<br/>Go to next page (${currentPage + 1} of ${lastPage})?`,
                    showCancelButton: true,
                    confirmButtonText: 'Next Page',
                    cancelButtonText: 'Stay here',
                });
                if (next.isConfirmed) {
                    loadCategoryPage(category, currentPage + 1, { resetPending: false });
                } else if (isCategorySelected(category)) {
                    loadCategoryPage(category, currentPage, { resetPending: false });
                }
            } else {
                await Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: data.successMessage || 'Stock adjustment saved.',
                });
                if (scope === 'all' || !isCategorySelected(category) || currentPage >= lastPage) {
                    history.push('/item-stock-adjustment');
                } else if (isCategorySelected(category)) {
                    loadCategoryPage(category, currentPage, { resetPending: false });
                }
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Request failed.' });
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await saveAdjustment('page');
    };

    const dateBounds = getInventoryDateBounds(project?.inventoryBackdateDays);
    const pageChangedCount = adjustmentRows.filter(isChangedRow).length;
    const manualChangedCount = manualRows.filter(isChangedRow).length;
    const allPendingCount = (() => {
        const map: PendingMap = { ...pendingChanges };
        adjustmentRows.forEach((r) => { if (isChangedRow(r)) map[rowKey(r, isFinishedProduct)] = r; });
        manualRows.forEach((r) => { if (isChangedRow(r)) map[rowKey(r, isFinishedProduct)] = r; });
        return Object.values(map).filter(isChangedRow).length;
    })();
    const otherPagesPending = Math.max(0, allPendingCount - pageChangedCount - manualChangedCount);
    const rangeFrom = categoryTotal === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeTo = Math.min(currentPage * PAGE_SIZE, categoryTotal);

    const renderRows = (rows: AdjustmentRow[], source: 'page' | 'manual', slOffset = 0) =>
        rows.map((row, index) => {
            const adjQty = getAdjQty(row);
            return (
                <tr key={`${source}-${rowKey(row, isFinishedProduct)}`} className={isChangedRow(row) ? 'table-warning' : undefined}>
                    <td className="text-center">{slOffset + index + 1}</td>
                    <td><div className="fw-semibold text-dark" title={row.itemName}>{row.itemName}</div></td>
                    {isFinishedProduct && <td>{row.packing || '-'}</td>}
                    {isFinishedProduct && <td>{row.size || '-'}</td>}
                    <td>{row.itemCode || '-'}</td>
                    <td>{row.categoryName || '-'}</td>
                    <td>{row.unitName || '-'}</td>
                    <td>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.averageRate === 0 || row.averageRate ? row.averageRate : ''}
                            onChange={(e) => {
                                const num = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                const nextRate = isNaN(num) ? 0 : (isStockValueAdjustment ? num : Math.max(0, num));
                                updateRowField(index, 'averageRate', nextRate, source);
                            }}
                            min={isStockValueAdjustment ? undefined : 0}
                            step="any"
                        />
                    </td>
                    <td className="text-end">{row.stockQuantity}</td>
                    <td>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={row.physicalQuantity === 0 || row.physicalQuantity ? row.physicalQuantity : ''}
                            onChange={(e) => {
                                const num = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                updateRowField(index, 'physicalQuantity', isNaN(num) ? 0 : Math.max(0, num), source);
                            }}
                            min={0}
                            step="any"
                        />
                    </td>
                    <td>
                        <span className={`fw-bold ${adjQty > 0 ? 'text-success' : adjQty < 0 ? 'text-danger' : 'text-muted'}`}>
                            {adjQty > 0 ? '+' : ''}{adjQty}
                        </span>
                    </td>
                    <td>
                        <button
                            type="button"
                            className="btn btn-light-danger btn-sm"
                            onClick={() => (source === 'manual' ? removeManualRow(index) : removePageRow(index))}
                        >
                            <KTSVG path='/media/svg/icons/General/Trash.svg' className='svg-icon-5' />
                        </button>
                    </td>
                </tr>
            );
        });

    return (
        <div className="card card-custom">
            <div className="card-header">
                <div className="card-title">
                    <KTSVG path='/media/svg/icons/Home/Book-open.svg' className='svg-icon-1 svg-icon-primary' />
                    <span className="text fw-bolder" style={{ marginLeft: '10px' }}>Stock Adjustment</span>
                </div>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Item Type <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select value={itemType} onChange={handleItemTypeChange} options={ITEM_TYPE_OPTIONS} placeholder="Select Item Type" menuPosition="fixed" styles={{ menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Project <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select value={project} onChange={handleProjectChange} options={projects} placeholder="Select Project" menuPosition="fixed" styles={{ menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Warehouse <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select value={warehouse} onChange={handleWarehouseChange} options={warehouses} placeholder="Select Warehouse" menuPosition="fixed" styles={{ menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }} isDisabled={!project?.value} />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Date <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={adjustment.adjustmentDate}
                                min={dateBounds.minDate || undefined}
                                max={dateBounds.maxDate}
                                onChange={(e) => {
                                    const dateVal = clampToInventoryDateBounds(e.target.value, project?.inventoryBackdateDays);
                                    setAdjustment((p) => ({ ...p, adjustmentDate: dateVal }));
                                    if (isCategorySelected(category) && project?.value && warehouse?.value) {
                                        loadCategoryPage(category, 1, { adjustmentDate: dateVal, resetPending: true });
                                    } else clearAll();
                                }}
                                disabled={!project?.value}
                                required
                            />
                            <div className="form-text text-muted mt-1">
                                {inventoryDateHint(!!project?.value, project?.inventoryBackdateDays)}
                            </div>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">{categoryLabel}</label>
                        <div className="col-lg-4">
                            <Select
                                value={category}
                                onChange={handleCategoryChange}
                                options={categories}
                                placeholder={`Select ${categoryLabel}`}
                                isClearable
                                menuPosition="fixed"
                                styles={{ menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }}
                                isDisabled={!project?.value || !warehouse?.value}
                            />
                            <div className="form-text text-muted mt-1">
                                {PAGE_SIZE} per page. Update page → Save This Page → Next Page. Or search & add any missing item.
                                {categoryTotal > 0 ? (
                                    <span className="fw-bold text-dark"> Total: {categoryTotal.toLocaleString()}</span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">{statusLabel}</label>
                        <div className="col-lg-4">
                            <Select
                                value={status}
                                onChange={handleStatusChange}
                                options={STATUS_OPTIONS}
                                placeholder={`Select ${statusLabel}`}
                                menuPosition="fixed"
                                styles={{ menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }}
                                isDisabled={!project?.value || !warehouse?.value}
                            />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Adjustment Type <span className="text-danger">*</span></label>
                        <div className="col-lg-4">
                            <Select value={adjustmentType} onChange={handleAdjustmentTypeChange} options={adjustmentTypes} placeholder="Select Adjustment Type" menuPosition="fixed" styles={{ menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }} />
                        </div>
                    </div>
                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Remarks</label>
                        <div className="col-lg-4">
                            <textarea className="form-control form-control-sm" rows={2} value={adjustment.remarks} onChange={(e) => setAdjustment((p) => ({ ...p, remarks: e.target.value }))} />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <label className="col-lg-2 col-form-label text-end">Add {isFinishedProduct ? 'Product' : 'Item'}</label>
                        <div className="col-lg-6">
                            <AsyncSelect
                                key={`add-${addSelectKey}-${itemType?.value}-${category?.value || 'all'}-${status?.value}`}
                                cacheOptions={false}
                                defaultOptions={!!category?.value}
                                loadOptions={loadItemOptions}
                                onInputChange={(val) => { itemSearchInputRef.current = val ?? ''; }}
                                onChange={handleAddItem}
                                value={null}
                                placeholder={searchPlaceholder}
                                menuPosition="fixed"
                                styles={{ control: (p: any) => ({ ...p, minHeight: '36px' }), menuPortal: (b: any) => ({ ...b, zIndex: 9999 }) }}
                                menuPortalTarget={document.body}
                                isDisabled={!canSearchItems}
                            />
                        </div>
                    </div>

                    <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
                        <h6 className="mb-0">
                            {detailsTitle}
                            {loadingCategoryItems && <span className="text-muted ms-2 fs-7 fw-normal">Loading...</span>}
                            {!loadingCategoryItems && category?.value && (
                                <span className="text-muted ms-2 fs-7 fw-normal">
                                    ({rangeFrom}-{rangeTo} of {categoryTotal.toLocaleString()}
                                    {pageChangedCount ? `, ${pageChangedCount} changed on page` : ''}
                                    {otherPagesPending ? `, ${otherPagesPending} pending on other pages` : ''})
                                </span>
                            )}
                        </h6>
                        <div className="d-flex align-items-center">
                            {category?.value && (
                                <span className="badge badge-light-primary me-2">
                                    {category.label} · Page {currentPage}/{lastPage}
                                </span>
                            )}
                            {(adjustmentRows.length > 0 || manualRows.length > 0) && (
                                <button type="button" className="btn btn-light-danger btn-sm" onClick={() => {
                                    Swal.fire({ title: 'Clear all?', text: 'Clear table and pending edits.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes' })
                                        .then((r) => { if (r.isConfirmed) clearAll(); });
                                }}>
                                    Clear All
                                </button>
                            )}
                        </div>
                    </div>

                    {category?.value && lastPage > 1 && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 p-3 bg-light rounded border">
                            <div className="text-muted small">
                                Showing <b>{rangeFrom}-{rangeTo}</b> of <b>{categoryTotal.toLocaleString()}</b>
                                {otherPagesPending > 0 && (
                                    <span className="text-warning ms-2">({otherPagesPending} unsaved on other pages)</span>
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button type="button" className="btn btn-sm btn-light-primary" disabled={currentPage <= 1 || loadingCategoryItems} onClick={() => goToPage(currentPage - 1)}>
                                    ← Previous
                                </button>
                                <span className="mx-2 fw-bold">Page {currentPage} of {lastPage}</span>
                                <button type="button" className="btn btn-sm btn-light-primary" disabled={currentPage >= lastPage || loadingCategoryItems} onClick={() => goToPage(currentPage + 1)}>
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}

                    <div style={{ maxHeight: 480, overflowY: 'auto' }}>
                        <Table size="sm" bordered hover className="mb-0">
                            <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: '4%' }} className="text-center">SL</th>
                                    <th style={{ width: isFinishedProduct ? '18%' : '24%' }}>{nameColumnLabel}</th>
                                    {isFinishedProduct && <th style={{ width: '8%' }}>Packing</th>}
                                    {isFinishedProduct && <th style={{ width: '6%' }}>Size</th>}
                                    <th style={{ width: '12%' }}>{codeColumnLabel}</th>
                                    <th style={{ width: '12%' }}>Category</th>
                                    <th style={{ width: '8%' }}>Unit</th>
                                    <th style={{ width: '10%' }}>Avg. Rate</th>
                                    <th style={{ width: '10%' }}>Prev. Stock</th>
                                    <th style={{ width: '10%' }}>Physical Qty</th>
                                    <th style={{ width: '10%' }}>Adjustment Qty</th>
                                    <th style={{ width: '6%' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {manualRows.length > 0 && (
                                    <>
                                        <tr className="table-info">
                                            <td colSpan={tableColSpan} className="fw-bold py-2">Manually added (not on this page)</td>
                                        </tr>
                                        {renderRows(manualRows, 'manual')}
                                    </>
                                )}
                                {adjustmentRows.length === 0 && manualRows.length === 0 && (
                                    <tr>
                                        <td colSpan={tableColSpan} className="text-center text-muted py-4">
                                            Select a category or All to load page 1 ({PAGE_SIZE} items), or search & add.
                                        </td>
                                    </tr>
                                )}
                                {renderRows(adjustmentRows, 'page', (currentPage - 1) * PAGE_SIZE)}
                            </tbody>
                        </Table>
                    </div>

                    {category?.value && lastPage > 1 && (
                        <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                            <button type="button" className="btn btn-sm btn-light-primary" disabled={currentPage <= 1 || loadingCategoryItems} onClick={() => goToPage(currentPage - 1)}>← Previous</button>
                            <span className="mx-2">Page {currentPage} / {lastPage}</span>
                            <button type="button" className="btn btn-sm btn-light-primary" disabled={currentPage >= lastPage || loadingCategoryItems} onClick={() => goToPage(currentPage + 1)}>Next →</button>
                        </div>
                    )}

                    <div className="d-flex flex-wrap justify-content-end align-items-center mt-3 gap-2">
                        {allPendingCount > pageChangedCount + manualChangedCount && (
                            <button
                                type="button"
                                className="btn btn-light-primary btn-sm me-2"
                                disabled={saving || allPendingCount === 0}
                                onClick={() => saveAdjustment('all')}
                            >
                                Save All Pending ({allPendingCount})
                            </button>
                        )}
                        <button
                            type="submit"
                            className="btn btn-primary btn-sm me-2"
                            disabled={saving || (pageChangedCount + manualChangedCount) === 0}
                        >
                            {saving ? 'Saving...' : `Save This Page${pageChangedCount + manualChangedCount > 0 ? ` (${pageChangedCount + manualChangedCount})` : ''}`}
                        </button>
                        <Link to="/item-stock-adjustment" className="btn btn-secondary btn-sm">Cancel</Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ItemStockAdjustmentCreate;
